import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Link as GatsbyLink } from 'gatsby';

import { color, variants } from 'styles/utils';
import { getClosestPageRegion } from 'utils';
import useTrackEvent from 'hooks/useTrackEvent';

function isRelative(url) {
  return /^[/#?]/.test(url);
}

const Link = styled(
  forwardRef(
    ({ children, href, onClick, popup, to, variant, ...props }, ref) => {
      const url = to || href;
      const isLocal = isRelative(url);
      const { trackEvent } = useTrackEvent();

      function handleClick(event) {
        if (typeof onClick === 'function') {
          onClick(event);
        }

        const link = event.currentTarget;

        // Remove trailing slashes for consistent comparisons.
        const pathname = link.pathname.replace(/\/*$/, '');

        if (
          link.hostname === window.location.hostname &&
          pathname === '/calendar'
        ) {
          trackEvent('demo_clicked', {
            object: 'demo',
            action: 'clicked',
            page_url: window.location.href,
            location: getClosestPageRegion(link) || 'unknown',
            label: link.textContent.trim(),
          });
        }

        if (
          link.hostname === 'app.sleuth.io' &&
          pathname === '/sleuth/sleuth/metrics/lead_time'
        ) {
          trackEvent('sleuth_demo_clicked', {
            location: getClosestPageRegion(link) || 'unknown',
            label: link.textContent.trim(),
          });
        }

        if (
          link.hostname === 'app.sleuth.io' &&
          pathname === '/account/signup'
        ) {
          trackEvent('signup_clicked', {
            object: 'signup',
            action: 'clicked',
            page_url: window.location.href,
            location: getClosestPageRegion(link) || 'unknown',
            label: link.textContent.trim(),
          });
        }

        if (url.startsWith('mailto:')) {
          trackEvent('mailto_clicked', {
            object: 'mailto',
            action: 'clicked',
            page_url: window.location.href,
            mailto_email: url.substring('mailto:'.length),
          });
        }

        if (popup && !url.startsWith('mailto:')) {
          event.preventDefault();
          const height = Math.min(window.screen.height, 500);
          const width = Math.min(window.screen.width, 500);
          const top = window.screen.height / 2 - height / 2;
          const left = window.screen.width / 2 - width / 2;
          window.open(
            url,
            '_blank',
            `popup=true,height=${height},width=${width},top=${top},left=${left}`
          );
        }
      }

      if (isLocal) {
        return (
          <GatsbyLink
            to={url}
            onClick={handleClick}
            ref={ref}
            variant={variant}
            {...props}
          >
            {children}
          </GatsbyLink>
        );
      }

      return (
        <a
          href={url}
          onClick={handleClick}
          ref={ref}
          rel="noreferrer"
          target="_blank"
          variant={variant}
          {...props}
        >
          {children}
        </a>
      );
    }
  )
)`
  color: inherit;

  ${variants({
    fill: `
      &::after {
        content: '';
        inset: 0;
        position: absolute;
        z-index: 1;
      }
    `,
    inline: css`
      color: ${color('teal')};
      text-decoration: underline;
    `,
  })}
`;

export default Link;
